import React, { JSX, useEffect, useState } from "react";
import { Staff } from "../Table/StaffTable";
import { selectUser } from "../../redux/slices/authSlice";
import { useSelector } from "react-redux";

interface PersonalInfoProps {
    staff: Staff;
    roleList: any;
    selectedRole: string;
    onRoleChange: (roleId: string) => Promise<void>;
    rolesLoading: boolean;
    renderInput?: (label: string, value?: string | number) => JSX.Element;
}

const ReadOnlyBadge = () => (
    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] bg-gray-100 text-gray-400 border border-gray-200 px-2 py-0.5 rounded-full font-medium">
        Read only
    </span>
);

const StaticPersonalInfo: React.FC<PersonalInfoProps> = ({
    staff,
    selectedRole,
    onRoleChange,
    rolesLoading,
}) => {
    const currentUser = useSelector(selectUser);
    const isSuperAdmin = currentUser?.role?.name === "Super Admin";
    
    const fullName = staff.name || "";
    const initialFirstName = fullName.split(" ")[0] || "";
    const initialLastName = fullName.split(" ").slice(1).join(" ") || "";

    const [firstName, setFirstName] = useState(initialFirstName);
    const [lastName, setLastName] = useState(initialLastName);
    const [phoneNumber, setPhoneNumber] = useState(staff.phone_number || "");
    const [gender, setGender] = useState(staff.gender || "");
    const [branch, setBranch] = useState(staff.branch || "");
    const [isDirty, setIsDirty] = useState(false);

    useEffect(() => {
        const hasChanged =
            firstName !== initialFirstName ||
            lastName !== initialLastName ||
            phoneNumber !== (staff.phone_number || "") ||
            gender !== (staff.gender || "") ||
            branch !== (staff.branch || "");
        setIsDirty(hasChanged);
    }, [firstName, lastName, phoneNumber, gender, branch, staff, initialFirstName, initialLastName]);

    const formatDate = (dateString?: string) => {
        if (!dateString) return "N/A";
        return new Date(dateString).toLocaleDateString("en-GB", {
            day: "2-digit", month: "short", year: "numeric",
        });
    };

    const readOnlyClass =
        "w-full border border-gray-100 rounded-xl px-4 py-2.5 pr-24 text-sm text-gray-400 bg-gray-50 cursor-not-allowed";

    return (
        <div className="space-y-6">

            {/* ── Basic Information ─────────────────────────── */}
            <section className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">

                {/* Section header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#6226EF]/5 to-transparent">
                    <span className="w-1.5 h-6 bg-[#6226EF] rounded-full" />
                    <div>
                        <h3 className="text-sm font-semibold text-gray-800">Basic Information</h3>
                        <p className="text-xs text-gray-400 mt-0.5">See personal details below</p>
                    </div>
                </div>

                <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-5">

                    {/* First Name */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            First Name
                        </label>
                        <div className="relative">
                            <input value={firstName} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Last Name */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Last Name
                        </label>
                        <div className="relative">
                            <input value={lastName} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Phone */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Phone Number
                        </label>
                        <div className="relative">
                            <input value={phoneNumber} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Email — read only */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Email Address
                        </label>
                        <div className="relative">
                            <input value={staff.email || ""} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Gender */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Gender
                        </label>
                        <div className="relative">
                            <input value={gender} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Branch — now editable */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Branch
                        </label>
                        <div className="relative">
                            <input value={branch} readOnly className={readOnlyClass} />
                            <ReadOnlyBadge />
                        </div>
                    </div>

                    {/* Date Joined — read only, full width */}
                    <div className="flex flex-col gap-1.5 sm:col-span-2">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Date Joined
                        </label>
                        <div className="relative">
                            <input
                                value={formatDate(staff.dateCreated)}
                                readOnly
                                className={readOnlyClass}
                            />
                            <ReadOnlyBadge />
                        </div>
                    </div>
                </div>

                {/* Footer / Save Button */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                    <p className="text-xs text-gray-400">
                        {isDirty ? "You have unsaved changes." : "All changes are saved."}
                    </p>
                    {/* <button
                        onClick={handleUpdate}
                        disabled={!isDirty || isLoading || !hasPermission("update staff")}
                        className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200
              ${isDirty && hasPermission("update staff")
                                ? "bg-[#6226EF] hover:bg-[#511ad4] shadow-lg shadow-[#6226EF]/25 active:scale-95"
                                : "bg-gray-200 text-gray-400 cursor-not-allowed"
                            }`}
                    >
                        {isLoading && <Spinner />}
                        {isLoading ? "Saving..." : "Save Changes"}
                    </button> */}
                </div>
            </section>

            {/* ── Role Management ───────────────────────────── */}
            <section className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">

                {/* Section header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#143887]/5 to-transparent">
                    <div className="flex items-center gap-3">
                        <span className="w-1.5 h-6 bg-[#143887] rounded-full" />
                        <div>
                            <h3 className="text-sm font-semibold text-gray-800">Role Management</h3>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {isSuperAdmin ? "Assign or change the staff member's role" : "Role assignment is restricted"}
                            </p>
                        </div>
                    </div>

                    {!isSuperAdmin && (
                        <span className="flex items-center gap-1.5 text-xs bg-amber-50 text-amber-600 border border-amber-200 px-3 py-1.5 rounded-full font-semibold">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            Super Admin only
                        </span>
                    )}
                </div>

                <div className="p-6">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            Assigned Role
                        </label>

                        <div className="relative">
                            <input
                                value={staff.role || "No role assigned"}
                                readOnly
                                className={readOnlyClass}
                            />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] bg-amber-50 text-amber-500 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                                <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                </svg>
                                Locked
                            </span>
                        </div>

                        {/* {isSuperAdmin ? (
                            <div className="flex gap-3 items-stretch">
                                <select
                                    value={localRole}
                                    onChange={(e) => {
                                        setLocalRole(e.target.value);
                                        setIsRoleDirty(e.target.value !== selectedRole);
                                    }}
                                    aria-readonly
                                    disabled={rolesLoading}
                                    className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#143887]/20 focus:border-[#143887] transition bg-white"
                                >
                                    <option value="">Select a role</option>
                                    {roleList.map((role: { id: string; name: string }) => (
                                        <option key={role.id} value={role.id}>
                                            {role.name}
                                        </option>
                                    ))}
                                </select>

                                <button
                                    onClick={handleRoleUpdate}
                                    disabled={!isRoleDirty || isUpdatingRole}
                                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition-all duration-200 whitespace-nowrap
                    ${isRoleDirty
                                            ? "bg-[#143887] hover:bg-[#0f2d6e] shadow-lg shadow-[#143887]/25 active:scale-95"
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                        }`}
                                >
                                    {isUpdatingRole && <Spinner />}
                                    {isUpdatingRole ? "Updating..." : "Update Role"}
                                </button>
                            </div>
                        ) : (
                            <div className="relative">
                                <input
                                    value={staff.role || "No role assigned"}
                                    readOnly
                                    className={readOnlyClass}
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-[10px] bg-amber-50 text-amber-500 border border-amber-200 px-2 py-0.5 rounded-full font-semibold">
                                    <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                                    </svg>
                                    Locked
                                </span>
                            </div>
                        )} */}

                        {/* {isSuperAdmin && (
                            <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                                <svg className="w-3 h-3 text-[#143887]" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                As Super Admin, you can reassign roles for any staff member.
                            </p>
                        )} */}
                    </div>
                </div>
            </section>
        </div>
    );
};

export default StaticPersonalInfo;